import { useCallback } from 'react'
import { socket } from '../socket/client.js'
import { useGameStore } from '../store/gameStore.js'
import { useUIStore } from '../store/uiStore.js'
import { useTheaterStore } from '../store/theaterStore.js'
import { useStatsStore } from '../store/statsStore.js'
import { trackEvent } from '../utils/analytics.js'
import type { Ship, Coord } from '@fleetduel/shared'
import { coordKey } from '@fleetduel/shared'

/**
 * Hook for dispatching game actions.
 * Validates locally first, then sends to server.
 */
export function useGame() {
  const { setMyShips } = useGameStore()

  const placeShips = useCallback((ships: Ship[]) => {
    setMyShips(ships)
    socket.emit('place_ships', { ships })
    trackEvent('ships_placed', { theater: useTheaterStore.getState().theaterId })
  }, [setMyShips])

  const performAction = useCallback((target: Coord) => {
    const { selectedAction, torpedoDirection } = useUIStore.getState()
    const { opponentBoard, torpedoCharges, sonarCharges } = useGameStore.getState()

    // Local validation
    if (selectedAction === 'strike') {
      const state = opponentBoard.get(coordKey(target))
      if (state && state !== 'unknown') {
        return // Already targeted
      }
    }

    if (selectedAction === 'torpedo' && torpedoCharges <= 0) return
    if (selectedAction === 'sonar' && sonarCharges <= 0) return

    // Build action
    switch (selectedAction) {
      case 'strike':
        socket.emit('action', { action: { type: 'strike', target } })
        break
      case 'torpedo':
        socket.emit('action', { action: { type: 'torpedo', target, direction: torpedoDirection } })
        break
      case 'sonar':
        socket.emit('action', { action: { type: 'sonar', center: target } })
        break
    }
  }, [])

  const createSinglePlayer = useCallback((difficulty: 'easy' | 'medium' | 'hard' = 'easy') => {
    const { theaterId, terrainEnabled } = useTheaterStore.getState()
    useStatsStore.getState().resetMatch(difficulty)
    socket.emit('create_single_player', { difficulty, theaterId, terrainEnabled })
    trackEvent('game_start', { theater: theaterId, difficulty })
  }, [])

  return { placeShips, performAction, createSinglePlayer }
}
